import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Layout } from "@/components/Layout";
import { useIsMobile } from "@/hooks/use-mobile";
import { Reveal } from "@/components/Reveal";
import { PromiseScroll } from "@/components/home/PromiseScroll";
import { BookingCtaButton } from "@/components/cta/BookingCtaButton";

import { ClaimMarquee } from "@/components/home/ClaimMarquee";
import simonMaiPortrait from "@/assets/simon-mai.webp.asset.json";
import heroVideoMp4 from "@/assets/hero-video.mp4.asset.json";

import buergermeisterCursorImage from "@/assets/creategy-buergermeisterwahl-cursor-v2.webp.asset.json";
import krisenkommunikationCursorImage from "@/assets/creategy-krisenkommunikation-2.webp.asset.json";
import gemeindekommunikationCursorImage from "@/assets/creategy-gemeindekommunikation.webp.asset.json";
import verbandskommunikationCursorImage from "@/assets/creategy-verbandskommunikation.webp.asset.json";
import politischeKommunikationCursorImage from "@/assets/creategy-politische-kommunikation.webp.asset.json";
import parteikampagnenCursorImage from "@/assets/creategy-parteikampagnen-hover.png.asset.json";
import direktkandidatenCursorImage from "@/assets/creategy-parteikampagnen.webp.asset.json";
import personalratwahlenCursorImage from "@/assets/creategy-personalratwahlen.webp.asset.json";


import { lazySection } from "@/lib/lazy-section";
import {
  testimonialsQueryOptions,
  clientLogosQueryOptions,
} from "@/lib/public-content.queries";

// Below-the-Fold: erst nach Hydration nachladen, Daten kommen aus dem
// Loader-Prefetch (ensureQueryData) im Query-Cache.
const TestimonialsSlider = lazySection(async () => {
  const m = await import("@/components/home/TestimonialsSlider");
  return { default: m.TestimonialsSlider };
}, { minHeight: 520 });
const ClientLogosMarquee = lazySection(async () => {
  const m = await import("@/components/home/ClientLogosMarquee");
  return { default: m.ClientLogosMarquee };
}, { minHeight: 160 });
const FinalCta = lazySection(async () => {
  const m = await import("@/components/contact/FinalCta");
  return { default: m.FinalCta };
}, { minHeight: 480 });

export const Route = createFileRoute("/")({
  // Beide Datensätze speisen ausschließlich Below-the-Fold-Sektionen, die erst
  // nach der Hydration nachgeladen werden. Deshalb NICHT awaiten: der Prefetch
  // wird angestoßen, das HTML geht sofort raus (kürzere TTFB / kritische Kette).
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(testimonialsQueryOptions);
    void context.queryClient.prefetchQuery(clientLogosQueryOptions);
  },
  errorComponent: () => (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 md:px-12 section-y-hero text-center">
        <h1 className="heading-h1">Seite konnte nicht geladen werden.</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Bitte versuchen Sie es später erneut.
        </p>
      </div>
    </Layout>
  ),
  notFoundComponent: () => (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 md:px-12 section-y-hero text-center">
        <h1 className="heading-h1">Seite nicht gefunden.</h1>
      </div>
    </Layout>
  ),
  head: () => ({
    meta: [
      { title: "creategy | Spezialagentur für politische Kommunikation" },
      {
        name: "description",
        content:
          "Überparteiliche Spezialagentur für politische Kommunikation: Wir gewinnen Wahlkämpfe und stärken Organisationen – mit Kreativität und Strategie.",
      },
      { property: "og:title", content: "creategy | Spezialagentur für politische Kommunikation" },
      {
        property: "og:description",
        content:
          "Überparteiliche Spezialagentur für politische Kommunikation: Wir gewinnen Wahlkämpfe und stärken Organisationen – mit Kreativität und Strategie.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://creategy.de/" },
      { property: "og:site_name", content: "creategy" },
      { property: "og:locale", content: "de_DE" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "creategy | Spezialagentur für politische Kommunikation" },
      {
        name: "twitter:description",
        content:
          "Überparteiliche Spezialagentur für politische Kommunikation: Wir gewinnen Wahlkämpfe und stärken Organisationen – mit Kreativität und Strategie.",
      },
    ],
    links: [{ rel: "canonical", href: "https://creategy.de/" }],
  }),

  component: HomePage,
});

type Product = {
  to: string;
  title: ReactNode;
  text: string;
  cursorImage?: { src: string; alt: string };
};

const wahlenProducts: Product[] = [
  {
    to: "/wahlen-gewinnen/buergermeisterwahl",
    title: "Bürgermeisterwahlen",
    text: "Der Chefsessel im Rathaus. Wir begleiten Kandidatinnen und Kandidaten vom ersten Strategiegespräch bis zum Wahltag – mit Full-Service oder als modulares Paket.",
    cursorImage: { src: buergermeisterCursorImage.url, alt: "" },
  },
  {
    to: "/wahlen-gewinnen/direktkandidaten",
    title: "Direktkandidaten",
    text: "Persönlichkeit schlägt Parteilinie – wenn man sie richtig kommuniziert. Wir entwickeln Kampagnen für Direktkandidaten, die im Wahlkreis überzeugen müssen.",
    cursorImage: { src: direktkandidatenCursorImage.url, alt: "" },
  },
  {
    to: "/wahlen-gewinnen/parteikampagnen",
    title: "Parteikampagnen",
    text: "Eine Partei ist mehr als ihr Spitzenkandidat. Wir entwickeln Kampagnen für Parteien und Fraktionen auf Kreis-, Landes- und Bundesebene.",
    cursorImage: { src: parteikampagnenCursorImage.url, alt: "" },
  },
  {
    to: "/wahlen-gewinnen/personalratswahl",
    title: "Personalratswahlen",
    text: "Mobilisierung, die zählt. Wir führen Gewerkschaften und Berufsverbände zum Erfolg bei der Personalratswahl.",
    cursorImage: { src: personalratwahlenCursorImage.url, alt: "" },
  },
];

const kommunizierenProducts: Product[] = [
  {
    to: "/politisch-kommunizieren/politische-kommunikation",
    title: "Politische Kommunikation",
    text: "Wer gehört werden will, muss kontinuierlich kommunizieren. Wir begleiten Parteien und Fraktionen in ihrer laufenden Öffentlichkeitsarbeit: strategisch, kreativ, zuverlässig.",
    cursorImage: { src: politischeKommunikationCursorImage.url, alt: "" },
  },
  {
    to: "/politisch-kommunizieren/verbandskommunikation",
    title: (
      <>
        <span className="md:hidden">Verbands&shy;kommunikation</span>
        <span className="hidden md:inline">Verbandskommunikation</span>
      </>
    ),
    text: "Relevanz ist kein Zufall. Wir helfen Verbänden, Branchenorganisationen und NGOs, ihre politischen Ziele mit der richtigen Kommunikation zu erreichen.",
    cursorImage: { src: verbandskommunikationCursorImage.url, alt: "" },
  },
  {
    to: "/politisch-kommunizieren/krisenkommunikation",
    title: "Krisenkommunikation",
    text: "Wenn es darauf ankommt, zählt jede Stunde. Wir helfen politischen Akteuren und Organisationen, in kritischen Situationen handlungsfähig zu bleiben.",
    cursorImage: { src: krisenkommunikationCursorImage.url, alt: "" },
  },
  {
    to: "/politisch-kommunizieren/gemeindekommunikation",
    title: (
      <>
        <span className="md:hidden">Gemeinde&shy;kommunikation</span>
        <span className="hidden md:inline">Gemeindekommunikation</span>
      </>
    ),
    text: "Bürgerinnen und Bürger erwarten heute mehr als ein Amtsblatt. Wir helfen Gemeinden, so zu kommunizieren, wie es die Zeit verlangt – und kennen viele von ihnen schon länger als seit gestern.",
    cursorImage: { src: gemeindekommunikationCursorImage.url, alt: "" },

  },
];


const promises = [
  {
    title: "Wir haben abgestimmt: nur Politik.",
    body: "Andere machen heute Kampagne, morgen Waschmittel. Wir haben uns seit dem ersten Tag festgelegt. Wer zu uns kommt, bekommt kein Team, das sich einarbeitet, sondern eines, das schon da war.",
  },
  {
    title: "Wir messen uns am Wahltag.",
    body: "Schöne Kampagnen gibt es viele. Über 72 Prozent Erfolgsquote gibt es einmal. Wir fragen nicht, wie es aussieht, sondern ob es gewinnt.",
  },
  {
    title: "Überparteilich. Aber nicht meinungslos.",
    body: "Wir haben keine Lieblingspartei. Aber wir haben eine Lieblingsidee: Demokratie. Wer für sie antritt, kann auf uns zählen. Wer sie in Frage stellt, nicht.",
  },
  {
    title: "Eine Stimme. Die antwortet.",
    body: "Kein Callcenter, kein Formular, kein Warten. Bei uns haben Sie einen festen immer Ihren Ansprechpartner, erreichbar per Telefon, Mail und WhatsApp.",
  },
];

const accentClasses = {
  wahlen: {
    text: "text-brand-wahlen",
  },
  kommunikation: {
    text: "text-brand-kommunikation",
  },
} as const;

function ProductAccordionList({
  products,
  accent,
}: {
  products: Product[];
  accent: "wahlen" | "kommunikation";
}) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mqPointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const mqDesktop = window.matchMedia("(min-width: 1024px)");
    const mqReduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      setEnabled(mqPointer.matches && mqDesktop.matches);
      setReduceMotion(mqReduced.matches);
    };
    update();
    mqPointer.addEventListener("change", update);
    mqDesktop.addEventListener("change", update);
    mqReduced.addEventListener("change", update);
    return () => {
      mqPointer.removeEventListener("change", update);
      mqDesktop.removeEventListener("change", update);
      mqReduced.removeEventListener("change", update);
    };
  }, []);



  return (
    <ul
      className="mt-6 border-t border-border/60 divide-y divide-border/60"
      onMouseLeave={() => setHoveredIndex(null)}
    >
      {products.map((p, i) => (
        <ProductAccordionItem
          key={p.to}
          p={p}
          accent={accent}
          expanded={enabled && hoveredIndex === i}
          onEnter={() => enabled && setHoveredIndex(i)}
          onFocus={() => enabled && setHoveredIndex(i)}
          onBlur={() => enabled && setHoveredIndex(null)}
          reduceMotion={reduceMotion}
        />
      ))}
    </ul>
  );
}

function ProductAccordionItem({
  p,
  accent,
  expanded,
  onEnter,
  onFocus,
  onBlur,
  reduceMotion,
}: {
  p: Product;
  accent: "wahlen" | "kommunikation";
  expanded: boolean;
  onEnter: () => void;
  onFocus: () => void;
  onBlur: () => void;
  reduceMotion: boolean;
}) {
  const a = accentClasses[accent];
  const revealDuration = reduceMotion ? "duration-0" : "duration-[400ms]";
  // Cursor-Bild erst mounten, wenn die Zeile mindestens einmal expandiert war.
  // Verhindert, dass alle 8 Bilder gleichzeitig in die Ladeschlange rutschen.
  const everExpandedRef = useRef(false);
  if (expanded) everExpandedRef.current = true;
  const shouldRenderImage = everExpandedRef.current;

  return (
    <li
      className="group relative"
      onMouseEnter={onEnter}
    >
      <Link
        to={p.to}
        className={`flex items-center gap-4 overflow-hidden px-4 py-6 md:gap-10 md:px-6 md:py-8 lg:gap-14 lg:py-0 transition-[height] ${revealDuration} ease-out ${
          expanded ? "lg:h-56" : "lg:h-28"
        }`}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {/* Title column — on tablet also carries the paragraph stacked underneath */}
        <div className="flex min-w-0 flex-1 items-center justify-between gap-4 md:flex-col md:items-start md:justify-center md:gap-3 md:pr-4 lg:flex-row lg:items-center lg:justify-start lg:gap-0 lg:w-[28rem] lg:flex-none">
          <h4 className={`font-display text-2xl font-extrabold md:text-3xl lg:text-4xl ${a.text}`}>
            {p.title}
          </h4>
          <ArrowUpRight
            className={`h-6 w-6 shrink-0 md:h-7 md:w-7 md:hidden ${a.text}`}
          />
          {/* Tablet-only paragraph stacked directly below the title */}
          <p
            className={`hidden md:block lg:hidden font-body text-base leading-relaxed ${a.text}`}
          >
            {p.text}
          </p>
        </div>

        {/* Image column — reserved space, revealed on hover (desktop only) */}
        <div className="hidden lg:block lg:ml-8 lg:w-[200px] lg:h-[200px] shrink-0">
          {p.cursorImage && shouldRenderImage && (
            <img
              src={p.cursorImage.src}
              alt=""
              draggable={false}
              loading="lazy"
              decoding="async"
              width={200}
              height={200}
              className={`block h-full w-full rounded-[4px] object-cover shadow-elegant transition-opacity ${revealDuration} ease-out ${
                expanded ? "opacity-100" : "opacity-0"
              }`}
              aria-hidden={!expanded}
            />
          )}
        </div>

        {/* Description column — desktop only (tablet paragraph lives under the title) */}
        <div className="hidden lg:flex flex-1 min-w-0 items-center lg:pr-12">
          <p
            className={`font-body text-base leading-relaxed ${a.text} transition-opacity ${revealDuration} ease-out ${
              expanded ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={!expanded}
          >
            {p.text}
          </p>
        </div>


        {/* Arrow column — pinned to the right edge, mirroring the h4's left padding */}
        <div className="hidden md:flex shrink-0 items-center ml-auto">
          <ArrowUpRight
            className={`h-6 w-6 md:h-7 md:w-7 ${a.text} transition-all ${revealDuration} ease-out lg:group-hover:rotate-45 ${
              expanded ? "opacity-100" : "opacity-100 lg:opacity-40"
            }`}
          />
        </div>
      </Link>
    </li>
  );
}










function HeroInlineVideo({ enabled = true }: { enabled?: boolean }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    if (!enabled || activated) return;
    if (typeof IntersectionObserver === "undefined") {
      setActivated(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setActivated(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px 0px", threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [activated, enabled]);

  return (
    <div ref={ref} className="block h-full w-full">
      {enabled && activated ? (
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          // @ts-expect-error – valid HTML attribute, not yet in React types
          fetchpriority="low"
          disablePictureInPicture
          disableRemotePlayback
          width={16}
          height={9}
          className="block h-full w-full object-cover"
        >
          {/* Nur MP4: kleiner als das WebM-Encoding und überall unterstützt. */}
          <source src={heroVideoMp4.url} type="video/mp4" />
        </video>
      ) : null}
    </div>
  );
}



function HomePage() {
  // Nur die Variante der aktuellen Breite mountet das Video – sonst würde die
  // per CSS versteckte Zwillingsinstanz die Datei ein zweites Mal laden.
  const isMobile = useIsMobile();
  return (
    <Layout>
      {/* 1. Hero */}
      <section aria-labelledby="hero">
        <div className="section-y-hero mx-auto w-full max-w-[1400px] px-6 text-center md:px-12">
          <h1
            id="hero"
            className="mx-auto max-w-7xl font-display font-extrabold leading-[1.02] tracking-tight text-5xl md:text-7xl lg:text-8xl text-primary"
          >
            {/* Desktop/Tablet */}
            <span className="hidden md:inline">
              <span className="font-display font-extrabold">Kreativität</span>{" "}
              <span className="font-display font-extrabold">und</span>{" "}
              <span
                role="img"
                aria-label="Kurzes dekoratives Video aus dem Arbeitsalltag von creategy"
                className="inline-block h-[0.9em] w-[1.6em] overflow-hidden rounded-md leading-none align-[-0.12em]"
              >
                <HeroInlineVideo enabled={!isMobile} />
              </span>
              <br />
              <span className="font-display font-extrabold">Strategie</span>{" "}
              <span className="font-body font-normal">im Dienst</span>
              <br />
              <span className="font-body font-normal">der Demokratie.</span>
            </span>
            {/* Mobile */}
            <span className="md:hidden">
              <span className="font-display font-extrabold">Kreativität</span>{" "}
              <span className="font-display font-extrabold">und</span>
              <br />
              <span className="font-display font-extrabold">Strategie</span>{" "}
              <span
                role="img"
                aria-label="Kurzes dekoratives Video aus dem Arbeitsalltag von creategy"
                className="inline-block h-[0.9em] w-[1.6em] overflow-hidden rounded-md leading-none align-[-0.12em]"
              >
                <HeroInlineVideo enabled={isMobile} />
              </span>
              <br />
              <span className="font-body font-normal">im Dienst der</span>
              <br />
              <span className="font-body font-normal">Demokratie.</span>
            </span>
          </h1>

          <BookingCtaButton
            variant="primary"
            className="fade-in-up mx-auto mt-10 self-center"
            style={{ animationDelay: "240ms" }}
            eventPath="creategy/erstgespraech"
            showPhoto={false}
          />
        </div>
      </section>

      {/* 3. Kundenlogos */}
      <ClientLogosMarquee />


      {/* 1b. Leistungen */}
      <section aria-labelledby="leistungen">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="leistungen" className="heading-h2">
              Unsere{" "}
              <span className="font-display font-extrabold">Leistungen.</span>
            </h2>
          </Reveal>

          <div className="mt-16">
            <Reveal>
              <h3 className="heading-h3 text-brand-wahlen">
                Wahlen gewinnen
              </h3>
            </Reveal>
            <ProductAccordionList products={wahlenProducts} accent="wahlen" />
          </div>

          <div className="mt-16">
            <Reveal>
              <h3 className="heading-h3 text-brand-kommunikation">
                Kommunikation
              </h3>
            </Reveal>
            <ProductAccordionList products={kommunizierenProducts} accent="kommunikation" />
          </div>
        </div>
      </section>




      {/* 4. Unser Versprechen */}
      <section aria-labelledby="versprechen">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="versprechen" className="heading-h2 max-w-3xl">
              Unser{" "}
              <span className="font-display font-extrabold">Versprechen.</span>
            </h2>
          </Reveal>
          <PromiseScroll promises={promises} />
        </div>
      </section>

      {/* Sub-Hero / Claim */}
      <section aria-labelledby="claim" className="relative">
        <div className="section-y grid w-full grid-cols-1 items-center gap-6 lg:grid-cols-[700px_1fr]">
          <div className="relative z-10 flex min-w-0 flex-col justify-center self-stretch bg-background px-6 md:px-12 lg:claim-pl lg:pr-0">
            <h2 id="claim" className="heading-h2 max-w-3xl text-primary">
              <span className="font-display font-extrabold">Spezialagentur</span>
              <br />
              <span className="font-body font-normal">für politischen</span>
              <br />
              <span className="font-body font-normal">Erfolg.</span>
            </h2>
            <div className="mt-10">
              <Link
                to="/ueber-uns"
                className="inline-flex items-center gap-1.5 border-b-2 border-primary pb-1 font-display text-base font-extrabold text-primary transition-opacity hover:opacity-80 md:text-lg"
              >
                Agentur kennenlernen
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="relative -mx-6 mt-10 h-[160px] overflow-hidden md:-mx-12 lg:hidden">
              <ClaimMarquee variant="mobile" className="h-full" />
            </div>
          </div>
          <div className="relative hidden min-w-0 overflow-hidden lg:block min-h-[320px] bg-background">
            <ClaimMarquee variant="desktop" className="h-[320px]" />
          </div>
        </div>
      </section>


      {/* 5. Testimonials */}
      <TestimonialsSlider />

      {/* Abschluss-CTA mit Zeeg */}
      <FinalCta
        zeegEventType="erstgespraech"
        title="Politischer Erfolg beginnt mit einem Gespräch."
        description="Ob Wahlkampf, laufende Kommunikation oder Krisenfall: In einem ersten Gespräch schauen wir gemeinsam auf Ihre Ausgangslage und zeigen Ihnen, was kommunikativ in Ihrem Vorhaben steckt. Kostenlos, unverbindlich und vertraulich."
        closingLine="Buchen Sie sich direkt einen Termin, der für Sie passt. Simon Mai freut sich auf das Gespräch mit Ihnen."
        closingPhoto={simonMaiPortrait.url}
      />



    </Layout>
  );
}
